import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

export interface DateRange {
  startDate: string;
  endDate: string;
  preset?: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

const PRESETS = [
  { id: 'today', label: 'Today', days: 0 },
  { id: 'yesterday', label: 'Yesterday', days: 1 },
  { id: 'last7', label: 'Last 7 Days', days: 7 },
  { id: 'last30', label: 'Last 30 Days', days: 30 },
  { id: 'custom', label: 'Custom Range', days: -1 },
];

function getPresetDates(presetId: string): { startDate: string; endDate: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatDate = (d: Date) => d.toISOString().split('T')[0];

  switch (presetId) {
    case 'today':
      return { startDate: formatDate(today), endDate: formatDate(today) };
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { startDate: formatDate(yesterday), endDate: formatDate(yesterday) };
    }
    case 'last7': {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { startDate: formatDate(start), endDate: formatDate(today) };
    }
    case 'last30': {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { startDate: formatDate(start), endDate: formatDate(today) };
    }
    default:
      return { startDate: formatDate(today), endDate: formatDate(today) };
  }
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(value.preset === 'custom');
  const [customStart, setCustomStart] = useState(value.startDate);
  const [customEnd, setCustomEnd] = useState(value.endDate);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetClick = (presetId: string) => {
    if (presetId === 'custom') {
      setShowCustom(true);
    } else {
      const dates = getPresetDates(presetId);
      onChange({ ...dates, preset: presetId });
      setShowCustom(false);
      setIsOpen(false);
    }
  };

  const handleCustomApply = () => {
    if (customStart && customEnd && customStart <= customEnd) {
      onChange({ startDate: customStart, endDate: customEnd, preset: 'custom' });
      setIsOpen(false);
    }
  };

  const getDisplayLabel = () => {
    if (value.preset && value.preset !== 'custom') {
      const preset = PRESETS.find(p => p.id === value.preset);
      return preset?.label || 'Select Range';
    }

    // Format custom range display
    const start = new Date(value.startDate + 'T00:00:00');
    const end = new Date(value.endDate + 'T00:00:00');
    const formatShort = (d: Date) =>
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (value.startDate === value.endDate) {
      return formatShort(start);
    }
    return `${formatShort(start)} - ${formatShort(end)}`;
  };

  return (
    <div className={`date-range-picker ${className}`} ref={dropdownRef}>
      <button
        className="date-range-trigger"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <Calendar size={14} />
        <span>{getDisplayLabel()}</span>
        <ChevronDown size={14} className={`chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="date-range-dropdown">
          <div className="preset-list">
            {PRESETS.map(preset => (
              <button
                key={preset.id}
                className={`preset-btn ${value.preset === preset.id ? 'active' : ''}`}
                onClick={() => handlePresetClick(preset.id)}
                type="button"
              >
                {preset.label}
              </button>
            ))}
          </div>

          {showCustom && (
            <div className="custom-range">
              <div className="custom-inputs">
                <div className="input-group">
                  <label>From</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    max={customEnd}
                  />
                </div>
                <div className="input-group">
                  <label>To</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    min={customStart}
                  />
                </div>
              </div>
              <button
                className="apply-btn"
                onClick={handleCustomApply}
                disabled={!customStart || !customEnd || customStart > customEnd}
                type="button"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        .date-range-picker {
          position: relative;
          display: inline-block;
        }

        .date-range-trigger {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          color: var(--text-primary);
          font-size: 13px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .date-range-trigger:hover {
          border-color: var(--accent-green);
        }

        .date-range-trigger svg {
          color: var(--text-secondary);
        }

        .date-range-trigger .chevron {
          transition: transform 0.15s;
        }

        .date-range-trigger .chevron.open {
          transform: rotate(180deg);
        }

        .date-range-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          min-width: 200px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 100;
          overflow: hidden;
        }

        .preset-list {
          display: flex;
          flex-direction: column;
        }

        .preset-btn {
          padding: 10px 14px;
          background: transparent;
          border: none;
          text-align: left;
          font-size: 13px;
          color: var(--text-primary);
          cursor: pointer;
          transition: background 0.15s;
        }

        .preset-btn:hover {
          background: var(--bg-primary);
        }

        .preset-btn.active {
          background: var(--accent-green-bg);
          color: var(--accent-green);
          font-weight: 500;
        }

        .custom-range {
          border-top: 1px solid var(--border-secondary);
          padding: 12px;
        }

        .custom-inputs {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }

        .input-group {
          flex: 1;
        }

        .input-group label {
          display: block;
          font-size: 11px;
          color: var(--text-secondary);
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .input-group input {
          width: 100%;
          padding: 6px 8px;
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          color: var(--text-primary);
          font-size: 12px;
        }

        .input-group input:focus {
          outline: none;
          border-color: var(--accent-green);
        }

        .apply-btn {
          width: 100%;
          padding: 8px;
          background: var(--accent-green);
          border: none;
          border-radius: var(--radius-small);
          color: white;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
        }

        .apply-btn:hover:not(:disabled) {
          background: #059669;
        }

        .apply-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default DateRangePicker;
